import { createSlice } from "@reduxjs/toolkit";
import logger from "../../art-logger-config";
import { getFromLocalStorage } from "../../util/localStorageUtils";

export const tableColumnDefinitions = {
  title: "Select visible columns",
  options: [
    {
      label: "ASIN Attributes",
      options: [
        {
          id: "announceDate",
          label: "Announce Date",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "onlineStreetDate",
          label: "Online Street Date",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "programName",
          label: "Program",
          editable: false,
          defaultVisible: true,
        },
        {
          id: "marketplace",
          label: "Marketplace",
          editable: false,
          defaultVisible: true,
        },
        { id: "asin", label: "ASIN", editable: false, defaultVisible: true },
        {
          id: "asinParty",
          label: "ASIN Partiness",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "asinType",
          label: "ASIN Type",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "baselineStatus",
          label: "Baseline Status",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "userDescription",
          label: "User Description",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "description",
          label: "Description",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "partNumber",
          label: "Part Number",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "productLineName",
          label: "Product Line Name",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bundleBaseComponent",
          label: "Bundle Base Component",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "catalogLiveness",
          label: "Catalog Liveness",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "liveness",
          label: "Liveness",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bundleComponents",
          label: "Bundle Components",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bundleComponentQuantity",
          label: "Bundle Component Quantity",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "productLineId",
          label: "Product Line Id",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "itemType",
          label: "Item Type",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "salesChannel",
          label: "Sales Channel",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "programId",
          label: "Program Id",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "upc",
          label: "UPC",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "dtcp",
          label: "DTCP",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "productionFamily",
          label: "Production Family",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "gtin",
          label: "GTIN",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "allowListedDeviceCode",
          label: "Allow Listed Device Code",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "allowListedDtid",
          label: "Allow Listed DTID",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "bomDtids",
          label: "BOM DTID(s)",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "domainName",
          label: "Domain Name",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "makoWeblabStatus",
          label: "Weblab Status",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "buyBoxStatus",
          label: "BuyBox Status",
          editable: true,
          defaultVisible: false,
        },
      ],
    },
    {
      label: "ASIN Validation Attributes",
      options: [
        {
          id: "validationStatus",
          label: "Validation Status Summary",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultHattrick",
          label: "Hattrick Validation Result",
          editable: true,
          defaultVisible: true,
        },
        {
          id: "validationOverallResultLVS",
          label: "LVS Validation Result",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "validationOverallResultManualDGL",
          label: "Manual DGL Validation Result",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "validationOverallResultManualRL",
          label: "Manual RL Validation Result",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "validationOverallResultManualInstock",
          label: "Manual Instock Validation Result",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "validationOverallResultManualOX",
          label: "Manual OX Validation Result",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "validationOverallResultManualGTM",
          label: "Manual GTM Validation Result",
          editable: true,
          defaultVisible: false,
        },
        {
          id: "validationOverallResultManualCS",
          label: "Manual CS Validation Result",
          editable: true,
          defaultVisible: false,
        },
      ],
    },
  ],
};

export const pageSelectorOptions = {
  title: "Select page size",
  options: [
    { value: 10, label: "10 Records" },
    { value: 30, label: "30 Records" },
    { value: 50, label: "50 Records" },
    { value: 100, label: "100 Records" },
    { value: 200, label: "200 Records" },
    { value: 500, label: "500 Records" },
  ],
};

interface PorAsinPageControlState {
  preferences: {
    pageSize: number;
    visibleContent: string[];
  };
  sort?: { sortingField: string };
  descending: boolean;
  pageNumber: number;
}

function getInitialPreferences() {
  const defaultInitialPreferences = {
    pageSize: 30,
    visibleContent: tableColumnDefinitions.options[0].options
      .filter((c) => c.defaultVisible)
      .map((c) => c.id),
  };

  try {
    const localPrefsString = getFromLocalStorage("ASIN_TABLE_PREFERENCES");
    if (localPrefsString !== null) {
      return JSON.parse(localPrefsString);
    }
  } catch (e: any) {
    logger.error(
      `Failed to load initial preferences for asin table: ${e.toString()}`
    );
  }
  return defaultInitialPreferences;
}

const porAsinPageControlInitialState: PorAsinPageControlState = {
  preferences: getInitialPreferences(),
  descending: true,
  pageNumber: 1,
};

const porAsinPageControl = createSlice({
  name: "porAsinPageControl",
  initialState: porAsinPageControlInitialState,
  reducers: {
    setPreferences(state, action) {
      state.preferences.pageSize = action.payload.pageSize;
      state.preferences.visibleContent = action.payload.visibleContent;
    },
    mergeVisibleContentPreferences(state, action) {
      const activeColumns = action.payload?.activeColumns ?? [];
      state.preferences.visibleContent = Array.from(
        new Set<string>([...state.preferences.visibleContent, ...activeColumns])
      );
    },
    setSort(state, action) {
      state.sort = action.payload.sortingColumn;
      state.descending = action.payload.isDescending;
    },
    setAsinPageNumber(state, action) {
      state.pageNumber = action.payload.pageNumber;
    },
  },
});

export const {
  setPreferences,
  mergeVisibleContentPreferences,
  setSort,
  setAsinPageNumber,
} = porAsinPageControl.actions;

export default porAsinPageControl.reducer;
